import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSButton from "views/system-design/DSButton";
import DSChip from "views/system-design/DSChip";
import DSTable from "views/system-design/Table/DSTable";
import DSTableHead from "views/system-design/Table/DSTableHead";
import DSTableBody from "views/system-design/Table/DSTableBody";
import DSTableRow from "views/system-design/Table/DSTableRow";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSTableContainer from "views/system-design/Table/DSTableContainer";
import PageHeader from "components/PageHeader";
import { WatchMode } from "../../../api/admin";
import {
  useClockFieldWatches,
  useDeleteClockFieldWatch,
  useSetClockFieldWatchActive,
} from "../hooks/useClockFieldWatches";
import CreateWatchModal from "./CreateWatchModal";

const QUERY_KEY = ["clock-field-watches"];

const formatTime = (ts: number | null | undefined) => {
  if (!ts) return "—";
  return new Date(ts).toLocaleString();
};

const formatValue = (value: unknown) => {
  if (value === null || value === undefined) return "—";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
};

const ClockFieldWatchers = () => {
  const {
    data: watches,
    isLoading,
    isFetching,
    isError,
    error,
  } = useClockFieldWatches();
  const [creating, setCreating] = useState(false);
  const { mutate: remove } = useDeleteClockFieldWatch();
  const { mutate: setActive } = useSetClockFieldWatchActive();
  const queryClient = useQueryClient();

  const handleDelete = (id: string, clockId: string) => {
    if (window.confirm(`Delete the watcher for clock "${clockId}"?`)) {
      remove(id);
    }
  };

  return (
    <DSBox>
      <PageHeader
        overline="Devices"
        title="Clock Field Watchers"
        subtitle="Alert on Firestore field changes for a specific clock (heartbeat online, or any field reaching a target value)"
      />

      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <DSCircularProgress />
        </DSBox>
      ) : isError ? (
        <DSBox sx={{ p: 2 }}>
          <DSTypography color="error">
            Failed to load watchers:{" "}
            {error instanceof Error ? error.message : "Unknown error"}
          </DSTypography>
        </DSBox>
      ) : (
        <DSBox>
          <DSBox
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
              alignItems: "center",
              gap: 1,
            }}
          >
            {isFetching && <DSCircularProgress size={18} />}
            <DSButton
              variant="contained"
              size="small"
              onClick={() => setCreating(true)}
            >
              New watcher
            </DSButton>
            <DSButton
              variant="outlined"
              size="small"
              disabled={isFetching}
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: QUERY_KEY })
              }
            >
              Refresh
            </DSButton>
          </DSBox>

          <DSTableContainer>
            <DSTable size="small">
              <DSTableHead>
                <DSTableRow>
                  <DSTableCell>Clock ID</DSTableCell>
                  <DSTableCell>Field</DSTableCell>
                  <DSTableCell>Mode</DSTableCell>
                  <DSTableCell>Target</DSTableCell>
                  <DSTableCell>Threshold</DSTableCell>
                  <DSTableCell>Last Fired</DSTableCell>
                  <DSTableCell align="right">Actions</DSTableCell>
                </DSTableRow>
              </DSTableHead>
              <DSTableBody>
                {watches?.map((watch) => {
                  const isHeartbeat = watch.mode === WatchMode.HeartbeatOnline;
                  return (
                    <DSTableRow
                      key={watch.id}
                      sx={{ opacity: watch.active ? 1 : 0.5 }}
                    >
                      <DSTableCell>
                        <DSTypography variant="body2" fontWeight={500}>
                          {watch.clockId}
                        </DSTypography>
                      </DSTableCell>
                      <DSTableCell>
                        <DSTypography variant="caption">
                          {watch.collectionName}.{watch.fieldPath}
                        </DSTypography>
                      </DSTableCell>
                      <DSTableCell>
                        <DSChip
                          size="small"
                          label={isHeartbeat ? "heartbeat online" : "change"}
                          color={isHeartbeat ? "success" : "default"}
                        />
                      </DSTableCell>
                      <DSTableCell>
                        <DSTypography variant="caption">
                          {isHeartbeat ? "—" : formatValue(watch.targetValue)}
                        </DSTypography>
                      </DSTableCell>
                      <DSTableCell>
                        <DSTypography variant="caption">
                          {watch.thresholdMinutes}m
                        </DSTypography>
                      </DSTableCell>
                      <DSTableCell>
                        <DSTypography variant="caption">
                          {formatTime(watch.lastFiredAt)}
                        </DSTypography>
                      </DSTableCell>
                      <DSTableCell align="right">
                        <DSBox
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <DSButton
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setActive({
                                id: watch.id,
                                active: !watch.active,
                              })
                            }
                          >
                            {watch.active ? "Pause" : "Resume"}
                          </DSButton>
                          <DSButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleDelete(watch.id, watch.clockId)
                            }
                          >
                            Delete
                          </DSButton>
                        </DSBox>
                      </DSTableCell>
                    </DSTableRow>
                  );
                })}
              </DSTableBody>
            </DSTable>
          </DSTableContainer>
        </DSBox>
      )}

      <CreateWatchModal open={creating} onClose={() => setCreating(false)} />
    </DSBox>
  );
};

export default ClockFieldWatchers;
